import { useEffect, useState } from "react";
import Main from "./Main";
import { useAuthDispatch, useAuthStore } from "./contexts/AuthContext";
import { me } from "./apiCalls/auth";
import { Navigate, Routes, Route, useLocation, useNavigate } from "react-router-dom";
import Home from "./pages/main/Home";
import AppDataLoader from "./controllers/AppDataLoader";
import FullPageController from "./navigation/FullPageController";
import LoadingContent from "./components/progress/LoadingContent";
import Terms from "./pages/terms/Terms";
import VerificationEmailSentPage from "./pages/main/VerificationEmailSentPage";
import AccountJoinPage from "./pages/self-registration/AccountJoinPage";
import AccountNotActivePage from "./pages/main/AccountNotActivePage";
import checkAccountValidity from "./helpers/getAccountStatus";
import useSessionCheck from "./useSessionCheck";
import { useWorkspaceDispatch } from "./contexts/WorkspaceContext";
import CustomRegisterPage from "./pages/main/CustomRegisterPage";
import CustomLoginPage from "./pages/main/CustomLoginPage";
import userLoadingPageHelper from "./helpers/userLoadingPageHelper";
import ResetPswdPage from "./pages/main/ResetPswdPage";
import { useTrack } from "./analytics/hooks";
import AlertPage from "./pages/main/AlertPage";

const handleGoogleCredentialResponse = (response) => {
  const googleToken = encodeURIComponent(response.credential);

  // Redirect the browser (not fetch) so it can follow Auth0 redirects safely without CROS issue
  window.location.href = `/auth/google-onetap-signin?googleToken=${googleToken}`;
};

function App() {
  const track = useTrack();
  const { isLoading, user, selectedAccount } = useAuthStore();
  const authDispatch = useAuthDispatch();
  const [alertEmail, setAlertEmail] = useState(null);
  const [alertMsg, setAlertMsg] = useState(null);
  const [directUser, setDirectUser] = useState({});
  const [selfRegAccStatus, setSelfRegAccStatus] = useState({
    selectedSchool: null,
    schoolList: [],
    createFromGoogle: false,
    createManully: false,
    newSchoolCreated: false,
    fromSearchPage: false,
    fromSelectPage: false,
  });
  const workspaceDispatch = useWorkspaceDispatch();
  const [inviteeEmail, setInviteeEmail] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();

  // Call the custom hook to check session status
  useSessionCheck();

  const curUrl = window.location.href;
  const pathArray = curUrl.split("/");

  // get url search params
  const params = new URLSearchParams(window.location.search);

  // get invite token and reset pswd token from params
  const invite = params.get("invite");
  const resetPswdToken = params.get("token");

  // Handle alert messages passed via URL query parameters from server-side redirects
  useEffect(() => {
    const queryAlertMsg = params.get("alertMsg");
    const queryAlertEmail = params.get("alertEmail");

    if (queryAlertMsg) {
      setAlertMsg(queryAlertMsg);
      if (queryAlertEmail) {
        setAlertEmail(queryAlertEmail);
      }
      // Clean the URL by removing the query parameters
      navigate("/", { replace: true });
    }
  }, []); // Only run once on initial load

  useEffect(() => {
    if (location.state?.alertMsg) {
      setAlertMsg(location.state.alertMsg);
      if (location.state.alertEmail) {
        setAlertEmail(location.state.alertEmail);
      }
      // Clear the state from history so the alert doesn't reappear on refresh
      // by replacing the current entry in the history stack.
      navigate("/", { replace: true, state: null });
    }
  }, [location.state, navigate]);

  useEffect(() => {
    if (isLoading) return;

    if (!user?.id && typeof window.google !== "undefined") {
      // Initialize Google One Tap if user is not authenticated
      window.google.accounts.id.initialize({
        client_id: process.env.REACT_APP_GOOGLE_CLIENT_ID,
        callback: handleGoogleCredentialResponse,
      });

      window.google.accounts.id.prompt();
    } else if (user?.id && typeof window.google !== "undefined") {
      // If the user is authenticated, explicitly cancel/dismiss the prompt
      // This is crucial to prevent it from showing up again
      window.google.accounts.id.cancel();
    }
  }, [user, isLoading]);

  //get which page to go
  useEffect(() => {
    const newDirectUser = userLoadingPageHelper(user, selectedAccount);
    setDirectUser(newDirectUser);

    if (user && selectedAccount && newDirectUser.alert_type) {
      track("user_access_alert_presented", {
        user,
        account: selectedAccount,
        alert_type: newDirectUser.alert_type,
      });
    }
  }, [user, selectedAccount, track]);

  useEffect(() => {
    // a function that checks for a logged in user and sets context accordingly
    const getLoggedInUser = async () => {
      const token = { invite, resetPswdToken };
      let user = await me(token);
      let unknownDashboard = false;
      let foundAccId = null;

      // Fetch the dashboard with the uniqueHash if the URL contains dashboard hash(this is on the workspace)
      // then set the selected account ids associated with that dashboard
      if (pathArray[4] === "dashboard" && pathArray[5]) {
        try {
          const res = await fetch(`/api/dashboards/uniqueHash/${pathArray[5]}`);
          if (!res.ok) {
            unknownDashboard = true;
          }

          const { data, trashed } = await res.json();
          if (trashed) {
            workspaceDispatch({
              type: "setTrashDashboardRedirect",
              payload: true,
            });
          }
          foundAccId = data.AccountId;
        } catch (error) {
          console.error("Error fetching dashboard:", error);
        }
      }

      if (user.alertEmail || user.message) {
        setAlertEmail(user.alertEmail);
        setAlertMsg(user.message);
        if (invite) setInviteeEmail(user.alertEmail); //set the invitee email when the invitation expired
      }

      if (user?.authInfo) {
        authDispatch({ type: "setAuth", payload: user });

        if (user?.id) {
          //check if the url has dashboard/xxx, if yes, set find the account, otherwise set to the first one
          const foundAcc =
            pathArray[4] === "dashboard" && !unknownDashboard
              ? user.Accounts.find((acc) => acc.id === foundAccId)
              : user.Accounts[0];

          authDispatch({
            type: "setSelectedAccount",
            payload: foundAcc,
          });
        }
        if (user?.id) authDispatch({ type: "setSelectedAccountPermissions" });

        // INITIATE INTERCOM WIDGET
        // configure a name for the user
        function configureName(user) {
          let name;
          if (user.firstName && user.lastName) {
            name = user.firstName + " " + user.lastName; //preference is to use First Last
          } else if (user.firstName || user.lastName) {
            // if that's not possible use First OR Last
            name = user.firstName || user.lastName;
          } else if (user.preferredName) {
            // if no first or last, use preferred name
            name = user.preferredName;
          } else {
            name = user.email; // if there's no first, last, or preferred... use email
          }
          return name;
        }

        // configure the company property
        const company = {
          id: user.isSuperAdmin
            ? "Innovare"
            : user.Accounts && user.Accounts[0]?.id,
          name: user.isSuperAdmin
            ? "Innovare"
            : user.Accounts && user.Accounts[0]?.name,
          plan: checkAccountValidity(user.Accounts && user.Accounts[0])?.plan,
        };

        // configure intercom settings in the window object
        window.intercomSettings = {
          api_base: "https://api-iam.intercom.io",
          app_id: process.env.REACT_APP_INTERCOM_APPID,
          name: configureName(user), // Full name
          user_id: user.id, // a UUID for your user
          email: user.email, // the email for your user
          created_at: user.createdAt, // Signup date as a Unix timestamp
          user_hash: user.intercomHash, // an Identity Verification user hash for your user,
          company: company,
        };

        // intercom provided code snippet - appId inserted from env
        function intercom() {
          var w = window;
          var ic = w.Intercom;
          if (typeof ic === "function") {
            ic("reattach_activator");
            ic("update", w.intercomSettings);
          } else {
            var d = document;
            var i = function () {
              i.c(arguments);
            };
            i.q = [];
            i.c = function (args) {
              i.q.push(args);
            };
            w.Intercom = i;
            var l = function () {
              var s = d.createElement("script");
              s.type = "text/javascript";
              s.async = true;
              s.src = `https://widget.intercom.io/widget/${process.env.REACT_APP_INTERCOM_APPID}`;
              var x = d.getElementsByTagName("script")[0];
              x.parentNode.insertBefore(s, x);
            };
            if (document.readyState === "complete") {
              l();
            } else if (w.attachEvent) {
              w.attachEvent("onload", l);
            } else {
              w.addEventListener("load", l, false);
            }
          }
        }
        intercom();
      } else if (user?.email) {
        authDispatch({ type: "setAuth", payload: user });
        if (invite) setInviteeEmail(user.email); //set the invitee email when the invitation not expired
      }

      //identify the user for analytics
      if (user?.id) window.analytics.identify(user.id);

      authDispatch({ type: "setIsLoading", payload: false });
    };
    getLoggedInUser();
  }, [authDispatch, selfRegAccStatus.newSchoolCreated, invite, resetPswdToken]);

  if (isLoading) return <LoadingContent iconStyle={{ color: "white" }} />;

  if (user?.deleted) {
    window.location.href = `${process.env.REACT_APP_URL}/logout`;
    return null;
  } //if user is deleted, redirect to logout page

  return (
    <>
      {!isLoading && user?.id ? (
        <>
          {directUser.toEmailVerificationPage && (
            <VerificationEmailSentPage email={user.email} />
          )}
          {directUser.toPendingToDeletePage && (
            <AccountNotActivePage user={user} />
          )}
          {directUser.toAccountNotActivePage && (
            <AccountNotActivePage user={user} />
          )}
          {directUser.toInvitationExpiredPage && (
            <AlertPage
              alertEmail={user.email}
              alertMsg={
                "You have an expired invitation. Please contact your administrator to reissue an invitation."
              }
            />
          )}
          {directUser.toRevokeRequestPage && (
            <Navigate to="/accountjoin" replace />
          )}
          {directUser.toRequestToJoinPage && (
            <Navigate to="/accountjoin" replace />
          )}
          {directUser.toRegisterPage && <Navigate to="/register" replace />}
          {directUser.toMainPage && (
            <>
              <AppDataLoader />
              <FullPageController />
              <Main />
            </>
          )}
          {directUser.toTCPage && <Navigate to="/terms" replace />}
          {directUser.toResetPswdPage && (
            <ResetPswdPage email={user.email} token={resetPswdToken} />
          )}
          <Routes>
            <Route path="/terms" element={<Terms />} />
            <Route
              path="/resetpswd"
              element={
                <ResetPswdPage email={user.email} token={resetPswdToken} />
              }
            />
            <Route
              path="/accountjoin"
              element={
                <AccountJoinPage
                  regAccStatus={selfRegAccStatus}
                  updateAccStatus={setSelfRegAccStatus}
                />
              }
            />
            <Route
              path="/register"
              element={<CustomRegisterPage user={user} />}
            />
          </Routes>
        </>
      ) : (
        <>
          {alertMsg && <Home alertEmail={alertEmail} alertMsg={alertMsg} />}
          <Routes>
            <Route path="*" element={<Home alertEmail={alertEmail} />} />
            <Route path="/login" element={<CustomLoginPage />} />
            <Route
              path="/register"
              element={
                <CustomRegisterPage invitationToken={invite} user={user} />
              }
            />
          </Routes>
        </>
      )}
    </>
  );
}

export default App;
